<template>
  <div v-if="network.can.send_message" class="messages-page mt-2">
    <div class="row g-2 p-4 pb-3">
      <div class="col-sm-12 col-md-12 col-lg-6 col-xl-6 d-flex">
        <h3 data-test="title_announcement" class="fw-bold text-primary">{{ $t('announcements') }}</h3>
      </div>
      <div class="col-sm-12 col-md-12 col-lg-6 col-xl-6 d-flex justify-content-end">
        <date-range-picker
          id="selected_date"
          ref="picker"
          v-model="selected_date"
          data-test="selected_date"
          opens="right"
          :ranges="date_ranges"
          :autoApply="true"
          :showDropdowns="true"
          :linkedCalendars="false"
          :locale-data="{ firstDay: 1, format: 'yyyy-mm-dd ' }"
          style="padding: 0.2rem 1rem !important; min-width: 277px;"
          @update="messagesFiltered"
        >
          <template v-slot:input="picker" style="min-width: 450px;">
            <i class="fas fa-calendar-alt"></i>&nbsp;&nbsp;
            {{ picker.startDate | date }} - {{ picker.endDate | date }}
          </template>
        </date-range-picker>
        <router-link :to="'/networks/' + this.network.slug + '/messages/create'">
          <button id="create_message" data-test="create_message" class="btn btn-primary me-2" style="padding: 0.425rem 1.5rem !important;">
            {{ $t('network.messages.CreateMessage') }}
          </button>
        </router-link>
        <router-link :to="'/networks/' + this.network.slug + '/templates'">
          <button id="manage_template" data-test="manage_template" class="btn btn-info float-end ms-2" style="padding: 0.425rem 1.5rem !important;">
            {{ $t('Index.manage-template') }}
          </button>
        </router-link>
      </div>
    </div>
    <div class="row" style="padding: 0 1.5rem;">
      <div class="col-1 text-center">
        <span ref="drafts" data-test="drafts_announcement" role="button" 
          :class="currentView == 'drafts' ? 'text-primary underlined' : ''" 
          class="tab" @click="changeTab('drafts')">{{ $t('drafts') }}</span>
      </div>
      <div class="col-2 text-center">
        <span ref="sent" data-test="sent_announcement" role="button" 
          :class="currentView == 'sent' ? 'text-primary underlined' : ''" 
          class="tab ms-3" @click="changeTab('sent')">Sent</span>
      </div>
      <div class="col-2 text-start">
        <span ref="scheduled" data-test="scheduled_announcement" role="button" 
          :class="currentView == 'scheduled' ? 'text-primary underlined' : ''" 
          class="tab ms-3" @click="changeTab('scheduled')">Scheduled</span>
      </div>
      <div class="col-7"></div><hr style="opacity: 0.5; height: 3px;">
    </div>
    <div class="row">
      <div
        v-infinite-scroll="loadMore"
        class="messages-section"
        :infinite-scroll-disabled="busy || isLoading || reachedEnd"
        infinite-scroll-distance="10"
        infinite-scroll-throttle-delay="1000"
      >
        <div v-if="currentView == 'drafts'">
          <table class="table border-bottom">
            <tbody id="message-list">
              <tr v-for="message in list" id="item-message-1" :key="message.id" style="border-style: hidden;">
                <MessageCard :character_limit="100" view="drafts" :displayDeliverStats="false" :message="message" :network="network" :local_timezone="local_timezone" @message-deleted="deleteMessage" />
              </tr>
            </tbody>
          </table>
          <div v-if="list.length === 0 && !busy" class="alert alert-success text-center mt-n1 mb-n2">
            <span>{{ $t('network.noMessagesFound') }}</span>
          </div>
        </div>
        <div v-if="currentView == 'sent'">
          <table class="table border-bottom">
            <tbody id="message-list">
              <tr v-for="message in list" id="item-message-2" :key="message.id" style="border-style: hidden;">
                <MessageCard :character_limit="100" view="sent" :displayDeliverStats="false" :message="message" :network="network" :local_timezone="local_timezone" />
              </tr>
            </tbody>
          </table>
          <div v-if="list.length === 0 && !busy" class="alert alert-success text-center mt-n1 mb-n2">
            <span>{{ $t('network.noMessagesFound') }}</span>
          </div>
        </div>
        <div v-if="currentView == 'scheduled'">
          <table class="table border-bottom">
            <tbody id="message-list">
              <tr v-for="message in list" id="item-message-3" :key="message.id" style="border-style: hidden;">
                <MessageCard :character_limit="100" view="scheduled" :displayDeliverStats="false" :message="message" :network="network" :local_timezone="local_timezone" @message-deleted="deleteMessage" />
              </tr>
            </tbody>
          </table>
          <div v-if="list.length === 0 && !busy" class="alert alert-success text-center mt-n1 mb-n2">
            <span>{{ $t('network.noMessagesFound') }}</span>
          </div>
        </div>
      </div>
      <div class="clearfix"></div>
      <div class="text-center mt-5">
        <div v-if="busy" class="text-center">
          <lottie-player
            autoplay
            loop
	          speed="2.5"
            src="/img/animations/custom_loader.json"
            style="height: 130px;"
            >
          </lottie-player>
          <p id="cancel-label">{{ $t('network.fetchingMore') }}</p>
        </div>
        <div v-if="reachedEnd && !busy" class="ms-3 text-secondary">
          {{ $t('network.reachedEnd') }}
        </div>
      </div>
    </div>
  </div>
  <div v-else class="messages-page">
    <div class="row">
      <div>
        <table class="table border-bottom mt-4">
          <tbody id="message-list">
            <tr v-for="message in list.filter(message => message.sent === true)" id="item-message-2" :key="message.id" style="border-style: hidden;">
              <MessageCard :character_limit="100" :displayDeliverStats="false" :message="message" :network="network" />
            </tr>
          </tbody>
        </table>
        <div v-if="list.filter(message => message.sent === true).length === 0 && !busy" class="alert alert-success text-center mt-n1 mb-n2">
          <span>{{ $t('network.noMessagesFound') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import MessageCard from './Card.vue';
import DateRangePicker from 'vue2-daterange-picker';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';
import infiniteScroll from 'vue-infinite-scroll';
import moment from 'moment';

export default {
  name: 'NetworkMessages',
  components: {
    MessageCard,
    DateRangePicker,
  },
  directives: { infiniteScroll },
  filters: {
    date(val) {
      return val ? moment(val).format('YYYY-MM-DD') : '';
    },
  },
  props: {
    network: {
      type: Object,
      default: null,
    },
  },
  data() {
    return this.initialState();
  },
  computed: {
    reachedEnd() {
      if (this.list && this.list.length >= this.pagination.total) {
        return true;
      }
      return false;
    }
  },
  mounted() {
    this.load();
    this.fetchTimezones();
  },
  beforeDestroy() {
    if (this.pendingRequest) {
      clearTimeout(this.pendingRequest);
    }
  },
  methods: {
    /**
     * @define To Initialize Necessary Variables
     */
    initialState() {
      return {
        list: [],
        busy: true,
        pagination: {
          limit: 10,
          offset: 0,
          total: 0, // the first request will tell us the total
        },
        currentView: this.$route.hash ? this.$route.hash.replace('#', '') : 'sent',
        hash: this.$route.hash,
        selected_date: {
          startDate: new Date(new Date().getFullYear(), 0, 1),
          endDate: moment().endOf('year').toDate(),
        },
        date_ranges: {
          Today: [new Date(), new Date()],
          'This week': [moment().startOf('week').toDate(), moment().endOf('week').toDate()],
          'This month': [moment().startOf('month').toDate(), moment().endOf('month').toDate()],
          'This year': [moment().startOf('year').toDate(), moment().endOf('year').toDate()],
          'Next week': [moment().add(1, 'week').startOf('week').toDate(), moment().add(1, 'week').endOf('week').toDate()],
          'Next month': [moment().add(1, 'month').startOf('month').toDate(), moment().add(1, 'month').endOf('month').toDate()],
          'Next year': [moment().add(1, 'year').startOf('year').toDate(), moment().add(1, 'year').endOf('year').toDate()]
        },
        local_timezone: null,
        isLoading: false,
        requestId: 0,
        pendingRequest: null,
      };
    },
    fetchTimezones() {
      this.$http.get('timezones').then(resp => {
        let browserTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (browserTZ == 'America/Buenos_Aires') {
          browserTZ = 'America/Argentina/Buenos_Aires';
        }
        this.local_timezone = resp.data.find(tz => tz.name == browserTZ).name;
      });
    },
    /**
     * @define To Declare Pagination Variables
     * @returns {{network_id: (*|null), offset: number, limit: (Number|number)}}
     */
    getMessageRequest() {
      return {
        offset: this.pagination.offset,
        limit: this.pagination.limit,
        network_id: this.network ? this.network.id : null,
        start_date: moment(this.selected_date.startDate).format('YYYY-MM-DD'),
        end_date: moment(this.selected_date.endDate).format('YYYY-MM-DD'),
        view: this.currentView,
      };
    },
    changeTab(view) {
      if (this.currentView !== view) {
        this.currentView = view;

        // remove hash from URL
        if (this.$route.hash) {
          this.$router.replace({
            path: this.$route.path,
            query: this.$route.query
          });
        }

        // clear any pending request
        if (this.pendingRequest) {
          clearTimeout(this.pendingRequest);
        }
        this.busy = true;

        // reset pagination and clear list immediately
        this.pagination.offset = 0;
        this.pagination.total = 0;
        this.list = [];

        // schedule the actual data load with a slight delay
        this.pendingRequest = setTimeout(() => {
          this.pendingRequest = null;
          this.load();
        }, 100);
      }
    },
    /**
     * @define To Fetch Messages and
     */
    async load() {
      console.log('in network feed LOAD');
      // return early if already loading
      if (this.isLoading) {
        console.log('Already loading, skipping request');
        return;
      }
      const currentRequestId = ++this.requestId;
      this.isLoading = true;
      this.busy = true;

      try {
        const resp = await this.$http.get('networks/' + this.network.slug + '/messages', {
          params: this.getMessageRequest(),
        });

        // only process if this is still the current request
        if (currentRequestId === this.requestId) {
          const { list, total } = resp.data;
          this.pagination.total = total;

          if (this.pagination.offset === 0) {
            this.list = list;
          } else {
            this.list.push(...list);
          }
          console.log(`Request #${currentRequestId} completed. Got ${list.length} items, total: ${total}`);
        } else {
          console.log(`Ignoring stale response from request #${currentRequestId}`);
        }
      } catch (error) {
        console.error('Error loading messages:', error);
      } finally {
        // only update loading state if this is the current request
        if (currentRequestId === this.requestId) {
          this.busy = false;
          this.isLoading = false;
        }
      }
    },
    /**
     * @define To increase pagination offset
     */
    loadMore() {
      // don't load more if we reached the end or already loading
      if (this.reachedEnd || this.isLoading) {
        return;
      }
      // increment the offset
      this.pagination.offset += this.pagination.limit;
      this.load();
    },
    deleteMessage(deletedMessage) {
      // remove the deleted message from the messages array
      this.list = this.list.filter(message => message.id !== deletedMessage.id);
    },
    messagesFiltered() {
      // clear any pending request
      if (this.pendingRequest) {
        clearTimeout(this.pendingRequest);
      }
      this.busy = true;

      // reset pagination and clear list immediately
      this.pagination.offset = 0;
      this.pagination.total = 0;
      this.list = [];

      // schedule the actual data load with a slight delay
      this.pendingRequest = setTimeout(() => {
        this.pendingRequest = null;
        this.load();
      }, 300);
    },
  },
};
</script>
<style scoped>
.messages-page .nav-pills .nav-item .nav-link {
  border-radius: 10px !important;
  margin-right: 10px;
  border: none;
}
.messages-page .nav-pills .nav-item .nav-link.active {
  background: none;
  font-weight: bolder;
  color: #926dde;
}
th {
  width: 150px;
  text-align: center;
  color: #5C6D80;
  font-weight: 600;
  font-size: 19px;
  line-height: 30px;
}
.tab {
  font-weight: 600;
  font-size: 20px;
}
.underlined {
  text-decoration-line: underline;
  text-decoration-thickness: 3px;
  text-underline-offset: 9px;
}
.tab:hover {
  color: #005f9a;
}
</style>
